import React, { useState, useEffect, useCallback, useRef } from 'react'
import API from '../../../service/api'
import './PushMonitor.css'

const formatAge = (seconds) => {
  if (seconds === null || seconds === undefined) return '-'
  if (seconds < 60) return seconds + 's'
  if (seconds < 3600) return Math.floor(seconds / 60) + 'm ' + (seconds % 60) + 's'
  return Math.floor(seconds / 3600) + 'h ' + Math.floor((seconds % 3600) / 60) + 'm'
}

const formatTime = (isoStr) => {
  if (!isoStr) return '-'
  const d = new Date(isoStr)
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

const formatDateTime = (isoStr) => {
  if (!isoStr) return '-'
  const d = new Date(isoStr)
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) + ' ' +
    d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

const ageClass = (sec) => {
  if (sec === null) return ''
  if (sec <= 300) return 'pm-age-ok'
  if (sec <= 1800) return 'pm-age-warn'
  return 'pm-age-stale'
}

const getTodayStr = () => {
  const d = new Date()
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
}

const exportToCSV = (headers, rows, filename) => {
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => {
      const str = String(cell === null || cell === undefined ? '' : cell)
      return str.includes(',') || str.includes('"') ? '"' + str.replace(/"/g, '""') + '"' : str
    }).join(','))
  ].join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  link.click()
  URL.revokeObjectURL(link.href)
}

const PushMonitor = () => {
  const [clients, setClients] = useState([])
  const [selectedClient, setSelectedClient] = useState('')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [activeTab, setActiveTab] = useState('dashboard')
  const [selectedDate, setSelectedDate] = useState(getTodayStr())
  const [vehicleLogs, setVehicleLogs] = useState(null)
  const [logsLoading, setLogsLoading] = useState(false)
  const [logsError, setLogsError] = useState(null)
  const intervalRef = useRef(null)

  // Fetch client list
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await API({ url: '/push-monitor/clients', method: 'GET', manageLoader: false })
        const list = res?.data?.data || []
        setClients(list)
        if (list.length > 0) {
          setSelectedClient(prev => prev || list[0].clientCode)
        }
      } catch (err) {
        console.error('Failed to fetch clients', err)
      }
    }
    fetchClients()
  }, [])

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    if (!selectedClient) return
    try {
      setLoading(true)
      setError(null)
      const res = await API({ url: `/push-monitor/dashboard?clientCode=${selectedClient}`, method: 'GET', manageLoader: false })
      if (res?.data?.success) {
        setData(res.data.data)
      } else {
        setError(res?.data?.error || 'Failed to load')
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [selectedClient])

  // Fetch vehicle logs
  const fetchVehicleLogs = useCallback(async () => {
    if (!selectedClient || !selectedDate) return
    try {
      setLogsLoading(true)
      setLogsError(null)
      const res = await API({ url: `/push-monitor/vehicle-logs?clientCode=${selectedClient}&date=${selectedDate}`, method: 'GET', manageLoader: false })
      if (res?.data?.success) {
        setVehicleLogs(res.data.data)
      } else {
        setLogsError(res?.data?.error || 'Failed to load vehicle logs')
      }
    } catch (err) {
      setLogsError(err?.response?.data?.error || err.message || 'Failed to load vehicle logs')
    } finally {
      setLogsLoading(false)
    }
  }, [selectedClient, selectedDate])

  // Fetch on client change
  useEffect(() => {
    if (selectedClient) fetchDashboard()
  }, [selectedClient, fetchDashboard])

  // Fetch vehicle logs when tab or date changes
  useEffect(() => {
    if (activeTab === 'vehicle-logs' && selectedClient && selectedDate) {
      fetchVehicleLogs()
    }
  }, [activeTab, selectedClient, selectedDate, fetchVehicleLogs])

  // Auto-refresh every 30s (dashboard only)
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    if (autoRefresh && selectedClient && activeTab === 'dashboard') {
      intervalRef.current = setInterval(fetchDashboard, 30000)
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [autoRefresh, selectedClient, activeTab, fetchDashboard])

  const summary = data?.summary
  const vehicles = data?.vehicles || []
  const timeline = data?.pushTimeline || []
  const logsSummary = vehicleLogs?.dailySummary
  const logsVehicles = vehicleLogs?.vehicleLogs || []

  const handleExportDashboard = () => {
    const headers = ['#', 'Vehicle No', 'Health', 'Gap Reason', 'OEM', 'Model', 'GPS Age', 'CAN Age', 'GPS Time', 'Lat', 'Lng', 'SOC', 'Speed', 'Odometer', 'Ignition', 'Skips (1h)']
    const rows = vehicles.map((v, i) => [
      i + 1, v.vehicleNo, v.health, v.gapReason || 'OK', v.oem || '-', v.model || '-',
      formatAge(v.secsSinceGps), formatAge(v.secsSinceCan), formatTime(v.gpsTimestamp),
      v.lat?.toFixed(4) || '-', v.lng?.toFixed(4) || '-',
      v.soc !== null ? v.soc.toFixed(0) + '%' : '-',
      v.speed !== null ? v.speed.toFixed(0) : '-',
      v.odometer !== null ? v.odometer.toFixed(0) : '-',
      v.ignition === true ? 'ON' : v.ignition === false ? 'OFF' : '-',
      v.skippedCountLastHour || 0
    ])
    exportToCSV(headers, rows, `push-monitor-${selectedClient}-${new Date().toISOString().slice(0, 10)}.csv`)
  }

  const handleExportLogs = () => {
    const headers = ['#', 'Vehicle No', 'Payload Count', 'Push Success', 'Skipped', 'Last Push Time']
    const rows = logsVehicles.map((v, i) => [
      i + 1, v.vehicleNo, v.payloadCount, v.pushSuccessCount, v.skippedCount,
      v.lastPushTime ? formatDateTime(v.lastPushTime) : '-'
    ])
    exportToCSV(headers, rows, `vehicle-logs-${selectedClient}-${selectedDate}.csv`)
  }

  return (
    <div className="pm-container">
      <div className="pm-header">
        <h5 className="pm-title">Push API Monitor</h5>
        {summary && activeTab === 'dashboard' && <span className="pm-last-check">Last checked: {formatTime(summary.checkedAt)}</span>}
      </div>

      <div className="pm-toolbar">
        <select className="pm-select" value={selectedClient} onChange={e => setSelectedClient(e.target.value)}>
          <option value="">Select Client</option>
          {clients.map(c => (
            <option key={c.clientCode} value={c.clientCode}>{c.clientCode} ({c.serviceId})</option>
          ))}
        </select>

        {activeTab === 'dashboard' && (
          <>
            <button className="pm-refresh-btn" onClick={fetchDashboard} disabled={loading}>
              {loading ? 'Loading...' : 'Refresh'}
            </button>
            <label className="pm-auto-label">
              <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
              Auto-refresh (30s)
            </label>
            {vehicles.length > 0 && (
              <button className="pm-export-btn" onClick={handleExportDashboard}>Export CSV</button>
            )}
          </>
        )}

        {activeTab === 'vehicle-logs' && (
          <>
            <input
              type="date"
              className="pm-date-input"
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              max={getTodayStr()}
            />
            <button className="pm-refresh-btn" onClick={fetchVehicleLogs} disabled={logsLoading}>
              {logsLoading ? 'Loading...' : 'Fetch Logs'}
            </button>
            {logsVehicles.length > 0 && (
              <button className="pm-export-btn" onClick={handleExportLogs}>Export CSV</button>
            )}
          </>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="pm-tabs">
        <button
          className={`pm-tab ${activeTab === 'dashboard' ? 'pm-tab-active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          Dashboard
        </button>
        <button
          className={`pm-tab ${activeTab === 'vehicle-logs' ? 'pm-tab-active' : ''}`}
          onClick={() => setActiveTab('vehicle-logs')}
        >
          Vehicle Logs
        </button>
      </div>

      {error && activeTab === 'dashboard' && <div className="pm-error">{error}</div>}
      {loading && !data && activeTab === 'dashboard' && <div className="pm-loading">Loading dashboard...</div>}
      {logsError && activeTab === 'vehicle-logs' && <div className="pm-error">{logsError}</div>}
      {logsLoading && !vehicleLogs && activeTab === 'vehicle-logs' && <div className="pm-loading">Loading vehicle logs...</div>}

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && summary && (
        <>
          {/* Summary Cards */}
          <div className="pm-summary">
            <div className="pm-card pm-card-blue">
              <p className="pm-card-value">{summary.totalVehicles}</p>
              <p className="pm-card-label">Total Vehicles</p>
            </div>
            <div className="pm-card pm-card-green">
              <p className="pm-card-value">{summary.greenCount}</p>
              <p className="pm-card-label">Healthy</p>
            </div>
            <div className="pm-card pm-card-yellow">
              <p className="pm-card-value">{summary.yellowCount}</p>
              <p className="pm-card-label">Warning</p>
            </div>
            <div className="pm-card pm-card-red">
              <p className="pm-card-value">{summary.redCount}</p>
              <p className="pm-card-label">Critical</p>
            </div>
            <div className="pm-card pm-card-green">
              <p className="pm-card-value">{summary.pushSuccessLastHour}</p>
              <p className="pm-card-label">Push Success (1h)</p>
            </div>
            <div className="pm-card pm-card-red">
              <p className="pm-card-value">{summary.pushFailLastHour}</p>
              <p className="pm-card-label">Push Failed (1h)</p>
            </div>
            <div className="pm-card pm-card-gray">
              <p className="pm-card-value">{summary.totalSkipsLastHour}</p>
              <p className="pm-card-label">Skipped (1h)</p>
            </div>
            <div className="pm-card pm-card-gray">
              <p className="pm-card-value">{summary.authErrorLastHour}</p>
              <p className="pm-card-label">Auth Errors (1h)</p>
            </div>
          </div>

          {/* Vehicle Table */}
          <div className="pm-table-wrapper">
            <table className="pm-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Vehicle No</th>
                  <th>Health</th>
                  <th>Gap Reason</th>
                  <th>OEM / Model</th>
                  <th>GPS Age</th>
                  <th>CAN Age</th>
                  <th>GPS Time</th>
                  <th>Lat</th>
                  <th>Lng</th>
                  <th>SOC</th>
                  <th>Speed</th>
                  <th>Odometer</th>
                  <th>Ignition</th>
                  <th>Skips (1h)</th>
                </tr>
              </thead>
              <tbody>
                {vehicles.map((v, i) => (
                  <tr key={v.vehicleNo} className={i % 2 === 0 ? 'pm-row-even' : 'pm-row-odd'}>
                    <td>{i + 1}</td>
                    <td style={{ fontWeight: 600 }}>{v.vehicleNo}</td>
                    <td>
                      <span className={`pm-health-dot pm-dot-${v.health}`}></span>
                      {v.health}
                    </td>
                    <td>
                      {v.gapReason
                        ? <span className="pm-gap-tag pm-gap-red">{v.gapReason}</span>
                        : <span className="pm-gap-tag pm-gap-none">OK</span>
                      }
                    </td>
                    <td>{v.oem || '-'} / {v.model || '-'}</td>
                    <td className={ageClass(v.secsSinceGps)}>{formatAge(v.secsSinceGps)}</td>
                    <td className={ageClass(v.secsSinceCan)}>{formatAge(v.secsSinceCan)}</td>
                    <td>{formatTime(v.gpsTimestamp)}</td>
                    <td>{v.lat?.toFixed(4) || '-'}</td>
                    <td>{v.lng?.toFixed(4) || '-'}</td>
                    <td>{v.soc !== null ? v.soc.toFixed(0) + '%' : '-'}</td>
                    <td>{v.speed !== null ? v.speed.toFixed(0) + ' km/h' : '-'}</td>
                    <td>{v.odometer !== null ? v.odometer.toFixed(0) : '-'}</td>
                    <td>{v.ignition === true ? 'ON' : v.ignition === false ? 'OFF' : '-'}</td>
                    <td>{v.skippedCountLastHour || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Push Timeline */}
          {timeline.length > 0 && (
            <>
              <p className="pm-timeline-title">Push Timeline (last 20)</p>
              <div className="pm-timeline">
                {timeline.map((t, i) => (
                  <div key={i} className={`pm-tl-item ${t.status === 'SUCCESS' ? 'pm-tl-success' : 'pm-tl-fail'}`}>
                    <span className="pm-tl-time">{formatTime(t.time)}</span>
                    {t.status} ({t.vehicleCount}v)
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Config Info */}
          <div className="pm-config-info">
            ServiceId: {summary.serviceId} | Transmit Freq: {summary.transmitFreqSec}s | In TMS: {summary.vehiclesInTmsCurrent}/{summary.totalVehicles}
          </div>
        </>
      )}

      {/* Vehicle Logs Tab */}
      {activeTab === 'vehicle-logs' && (
        <>
          {logsSummary && (
            <div className="pm-summary">
              <div className="pm-card pm-card-blue">
                <p className="pm-card-value">{logsSummary.totalLogs}</p>
                <p className="pm-card-label">Total Logs</p>
              </div>
              <div className="pm-card pm-card-green">
                <p className="pm-card-value">{logsSummary.totalSuccess}</p>
                <p className="pm-card-label">Success</p>
              </div>
              <div className="pm-card pm-card-red">
                <p className="pm-card-value">{logsSummary.totalFailed}</p>
                <p className="pm-card-label">Failed</p>
              </div>
              <div className="pm-card pm-card-red">
                <p className="pm-card-value">{logsSummary.totalAuthErrors}</p>
                <p className="pm-card-label">Auth Errors</p>
              </div>
              <div className="pm-card pm-card-gray">
                <p className="pm-card-value">{logsSummary.totalSkipped}</p>
                <p className="pm-card-label">Skipped</p>
              </div>
            </div>
          )}

          {logsVehicles.length > 0 && (
            <div className="pm-table-wrapper">
              <table className="pm-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Vehicle No</th>
                    <th>Payload Count</th>
                    <th>Push Success</th>
                    <th>Skipped</th>
                    <th>Last Push Time</th>
                  </tr>
                </thead>
                <tbody>
                  {logsVehicles.map((v, i) => (
                    <tr key={v.vehicleNo} className={i % 2 === 0 ? 'pm-row-even' : 'pm-row-odd'}>
                      <td>{i + 1}</td>
                      <td style={{ fontWeight: 600 }}>{v.vehicleNo}</td>
                      <td>{v.payloadCount}</td>
                      <td className={v.pushSuccessCount > 0 ? 'pm-age-ok' : ''}>{v.pushSuccessCount}</td>
                      <td className={v.skippedCount > 0 ? 'pm-age-warn' : ''}>{v.skippedCount}</td>
                      <td>{v.lastPushTime ? formatDateTime(v.lastPushTime) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!logsLoading && logsVehicles.length === 0 && vehicleLogs && (
            <div className="pm-loading">No vehicle logs found for {selectedDate}</div>
          )}
        </>
      )}
    </div>
  )
}

export default PushMonitor
